<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ArrowRight, Check, CloudUpload, Database, RefreshCw, ShieldCheck, TriangleAlert } from '@lucide/vue'
import { Badge, Button, Card, EmptyState, Modal, PageHeader, Spinner } from '@drama/ui'

import ScenarioPicker from '../components/ScenarioPicker.vue'
import { getScenarioRelease, listVideoPreparation, publishScenarioToProduction } from '../api/index.js'
import { useAdminAuth } from '../composables/useAdminAuth.js'

const { locale, t } = useI18n()
const auth = useAdminAuth()
const games = ref([])
const selectedScenarioId = ref('')
const loadingGames = ref(false)
const loadingRelease = ref(false)
const publishing = ref(false)
const error = ref('')
const publishError = ref('')
const detail = ref(null)
const confirmOpen = ref(false)
const result = ref(null)
let releaseLoadId = 0

const production = computed(() => detail.value?.production ?? null)
const productionReachable = computed(() => production.value?.status === 'reachable')
const isAdmin = computed(() => auth.account.value.role === 'admin')
const isSameVersion = computed(() => productionReachable.value
  && production.value.current_version_id === detail.value.stage.current_version_id)
const canPublish = computed(() => isAdmin.value && detail.value !== null && productionReachable.value && !publishing.value)

onMounted(loadGames)
watch(locale, loadGames)
watch(selectedScenarioId, scenarioId => {
  releaseLoadId += 1
  detail.value = null
  result.value = null
  publishError.value = ''
  if (scenarioId !== '') loadRelease(scenarioId)
})

async function loadGames() {
  loadingGames.value = true
  error.value = ''
  try {
    games.value = (await listVideoPreparation(locale.value)).items
      .filter(item => item.scenario_status === 'published' && item.current_version_id !== null)
    if (selectedScenarioId.value === '' && games.value.length > 0) {
      selectedScenarioId.value = games.value[0].scenario_id
    } else if (selectedScenarioId.value !== '') {
      await loadRelease(selectedScenarioId.value)
    }
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loadingGames.value = false
  }
}

async function loadRelease(scenarioId = selectedScenarioId.value) {
  const loadId = ++releaseLoadId
  loadingRelease.value = true
  error.value = ''
  try {
    const response = await getScenarioRelease(scenarioId)
    if (loadId !== releaseLoadId || scenarioId !== selectedScenarioId.value) return
    detail.value = response
  } catch (requestError) {
    if (loadId !== releaseLoadId) return
    error.value = requestError.message
  } finally {
    if (loadId === releaseLoadId) loadingRelease.value = false
  }
}

async function publish() {
  const releaseDetail = detail.value
  const scenarioId = releaseDetail.stage.scenario_id
  const sourceVersionId = releaseDetail.stage.current_version_id
  publishing.value = true
  publishError.value = ''
  try {
    result.value = await publishScenarioToProduction(scenarioId, sourceVersionId)
    confirmOpen.value = false
    if (selectedScenarioId.value === scenarioId) await loadRelease(scenarioId)
  } catch (requestError) {
    publishError.value = requestError.message
    confirmOpen.value = false
    if (selectedScenarioId.value === scenarioId) await loadRelease(scenarioId)
  } finally {
    publishing.value = false
  }
}

function retry() {
  if (selectedScenarioId.value === '') return loadGames()
  return loadRelease()
}

function productionBadgeVariant(status) {
  switch (status) {
    case 'reachable': return 'success'
    case 'not_configured': return 'warning'
    case 'unreachable': return 'danger'
    default: throw new Error(`Unhandled production release status: ${status}`)
  }
}

function releaseBadgeVariant(status) {
  switch (status) {
    case 'prepared': return 'neutral'
    case 'transferring':
    case 'applying': return 'warning'
    case 'succeeded': return 'success'
    case 'failed': return 'danger'
    default: throw new Error(`Unhandled release status: ${status}`)
  }
}

function formatDate(value) {
  return new Intl.DateTimeFormat(locale.value, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
}

function formatBytes(value) {
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${(value / 1024 / 1024).toFixed(1)} MB`
}
</script>

<template>
  <div class="game-releases vga-page" data-insp-path="apps/visual-game-admin/src/pages/GameReleases.vue">
    <PageHeader :title="t('visualGameAdmin.gameReleases.title')">
      <template #subtitle>{{ t('visualGameAdmin.gameReleases.subtitle') }}</template>
      <template #actions>
        <Button variant="secondary" size="sm" :loading="loadingGames || loadingRelease" @click="loadGames">
          <RefreshCw :size="14" />{{ t('visualGameAdmin.common.refresh') }}
        </Button>
      </template>
    </PageHeader>

    <Card class="game-releases__selector">
      <div class="game-releases__selector-copy">
        <span class="game-releases__eyebrow">{{ t('visualGameAdmin.gameReleases.selectLabel') }}</span>
        <h2>{{ t('visualGameAdmin.gameReleases.selectTitle') }}</h2>
        <p>{{ t('visualGameAdmin.gameReleases.selectHint') }}</p>
      </div>
      <ScenarioPicker
        v-model="selectedScenarioId"
        :items="games"
        :empty-label="t('visualGameAdmin.scenarioPicker.title')"
        :search-placeholder="t('visualGameAdmin.scenarioPicker.searchPlaceholder')"
        :empty-result="t('visualGameAdmin.scenarioPicker.emptyResult')"
      />
    </Card>

    <div v-if="loadingGames || loadingRelease" class="game-releases__state"><Spinner /></div>
    <Card v-else-if="error" class="game-releases__state game-releases__state--error">
      <TriangleAlert :size="24" />
      <p>{{ error }}</p>
      <Button variant="secondary" @click="retry">{{ t('visualGameAdmin.common.retry') }}</Button>
    </Card>
    <EmptyState
      v-else-if="selectedScenarioId === ''"
      :title="t('visualGameAdmin.gameReleases.emptyTitle')"
      :description="t('visualGameAdmin.gameReleases.emptyDescription')"
    />

    <template v-else-if="detail">
      <div class="game-releases__route" aria-hidden="true">
        <span>{{ t('visualGameAdmin.gameReleases.stage') }}</span>
        <div><i /><ArrowRight :size="18" /><i /></div>
        <span>{{ t('visualGameAdmin.gameReleases.production') }}</span>
      </div>

      <section class="game-releases__compare">
        <Card :padded="false" class="game-releases__environment game-releases__environment--stage">
          <header>
            <div class="game-releases__environment-icon"><Database :size="20" /></div>
            <div>
              <span>{{ t('visualGameAdmin.gameReleases.source') }}</span>
              <h2>{{ t('visualGameAdmin.gameReleases.stage') }}</h2>
            </div>
            <Badge variant="success" size="sm">{{ t('visualGameAdmin.gameReleases.published') }}</Badge>
          </header>
          <dl>
            <div><dt>{{ t('visualGameAdmin.gameReleases.version') }}</dt><dd>v{{ detail.stage.version }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.gameReleases.versionId') }}</dt><dd><code>{{ detail.stage.current_version_id }}</code></dd></div>
            <div><dt>{{ t('visualGameAdmin.gameReleases.publishedAt') }}</dt><dd>{{ formatDate(detail.stage.published_at) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.gameReleases.assets') }}</dt><dd>{{ detail.stage.assets }} · {{ formatBytes(detail.stage.asset_bytes) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.gameReleases.syncAssets') }}</dt><dd>{{ detail.stage.media_objects }} · {{ formatBytes(detail.stage.media_bytes) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.gameReleases.contentGraph') }}</dt><dd>{{ detail.stage.game_entities }} / {{ detail.stage.game_entity_assets }} / {{ detail.stage.prompts }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.gameReleases.videoResources') }}</dt><dd>{{ detail.stage.video_resources }} · {{ t('visualGameAdmin.gameReleases.intentVideos', { count: detail.stage.intent_video_resources }) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.gameReleases.translations') }}</dt><dd>{{ detail.stage.translations }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.gameReleases.checks') }}</dt><dd>{{ detail.stage.publish_checks }}</dd></div>
          </dl>
        </Card>

        <Card :padded="false" class="game-releases__environment">
          <header>
            <div class="game-releases__environment-icon"><CloudUpload :size="20" /></div>
            <div>
              <span>{{ t('visualGameAdmin.gameReleases.target') }}</span>
              <h2>{{ t('visualGameAdmin.gameReleases.production') }}</h2>
            </div>
            <Badge :variant="productionBadgeVariant(production.status)" size="sm">
              {{ t(`visualGameAdmin.gameReleases.connection.${production.status}`) }}
            </Badge>
          </header>
          <dl v-if="production.status === 'reachable'">
            <div><dt>{{ t('visualGameAdmin.gameReleases.version') }}</dt><dd>{{ production.exists ? `v${production.version}` : '—' }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.gameReleases.versionId') }}</dt><dd><code>{{ production.current_version_id ?? '—' }}</code></dd></div>
            <div><dt>{{ t('visualGameAdmin.gameReleases.publishedAt') }}</dt><dd>{{ production.published_at ? formatDate(production.published_at) : '—' }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.gameReleases.assets') }}</dt><dd>{{ production.assets }}</dd></div>
          </dl>
          <div v-else class="game-releases__connection-note">
            <TriangleAlert :size="18" />
            <p>{{ production.message ?? t(`visualGameAdmin.gameReleases.connectionHelp.${production.status}`) }}</p>
          </div>
        </Card>
      </section>

      <Card class="game-releases__action">
        <div class="game-releases__action-copy">
          <div class="game-releases__action-icon"><ShieldCheck :size="24" /></div>
          <div>
            <h2>{{ isSameVersion ? t('visualGameAdmin.gameReleases.resyncTitle') : t('visualGameAdmin.gameReleases.readyTitle') }}</h2>
            <p>{{ t('visualGameAdmin.gameReleases.scope') }}</p>
            <p v-if="!isAdmin" class="game-releases__read-only">{{ t('visualGameAdmin.gameReleases.adminRequired') }}</p>
          </div>
        </div>
        <Button variant="primary" :disabled="!canPublish" :loading="publishing" @click="confirmOpen = true">
          <CloudUpload :size="17" />{{ isSameVersion ? t('visualGameAdmin.gameReleases.resync') : t('visualGameAdmin.gameReleases.publish') }}
        </Button>
        <p v-if="publishError" class="game-releases__publish-error"><TriangleAlert :size="16" />{{ publishError }}</p>
      </Card>

      <Card v-if="result" class="game-releases__result">
        <div class="game-releases__result-icon"><Check :size="20" /></div>
        <div>
          <h3>{{ t('visualGameAdmin.gameReleases.successTitle') }}</h3>
          <p>{{ t('visualGameAdmin.gameReleases.successSummary', {
            version: result.version,
            copied: result.assets_copied,
            reused: result.assets_reused,
            size: formatBytes(result.bytes_copied),
          }) }}</p>
          <code>{{ result.current_version_id }}</code>
        </div>
      </Card>

      <Card class="game-releases__history">
        <header class="game-releases__history-header">
          <div>
            <h2>{{ t('visualGameAdmin.gameReleases.historyTitle') }}</h2>
            <p>{{ t('visualGameAdmin.gameReleases.historyHint') }}</p>
          </div>
          <Badge variant="neutral" size="sm">{{ detail.history.length }}</Badge>
        </header>
        <p v-if="detail.history.length === 0" class="game-releases__history-empty">
          {{ t('visualGameAdmin.gameReleases.historyEmpty') }}
        </p>
        <div v-else class="game-releases__history-list">
          <article v-for="release in detail.history" :key="release.id" class="game-releases__release">
            <div class="game-releases__release-head">
              <div>
                <strong>{{ release.source_version_id }}</strong>
                <code>{{ release.id }}</code>
              </div>
              <Badge :variant="releaseBadgeVariant(release.status)" size="sm">
                {{ t(`visualGameAdmin.gameReleases.releaseStatus.${release.status}`) }}
              </Badge>
            </div>
            <dl class="game-releases__release-meta">
              <div><dt>{{ t('visualGameAdmin.gameReleases.operator') }}</dt><dd>{{ release.requested_by }}</dd></div>
              <div><dt>{{ t('visualGameAdmin.gameReleases.startedAt') }}</dt><dd>{{ formatDate(release.started_at) }}</dd></div>
              <div><dt>{{ t('visualGameAdmin.gameReleases.transfer') }}</dt><dd>{{ release.assets_copied }} / {{ release.assets_reused }} · {{ formatBytes(release.bytes_copied) }}</dd></div>
            </dl>
            <p v-if="release.error_message" class="game-releases__release-error">{{ release.error_code }} · {{ release.error_message }}</p>
            <ol class="game-releases__logs">
              <li v-for="log in release.logs" :key="log.id">
                <span class="game-releases__log-dot" />
                <div><strong>{{ log.message }}</strong><small>{{ formatDate(log.created_at) }}</small></div>
              </li>
            </ol>
          </article>
        </div>
      </Card>
    </template>

    <Modal
      :open="confirmOpen"
      :title="t('visualGameAdmin.gameReleases.confirmTitle')"
      :close-on-backdrop="!publishing"
      width="min(92vw, 620px)"
      @update:open="confirmOpen = $event"
    >
      <div v-if="detail" class="game-releases__confirm">
        <div class="game-releases__confirm-version">
          <span>{{ detail.stage.title }}</span>
          <strong>v{{ detail.stage.version }}</strong>
          <code>{{ detail.stage.current_version_id }}</code>
        </div>
        <p>{{ t('visualGameAdmin.gameReleases.confirmScope', { assets: detail.stage.media_objects }) }}</p>
        <div class="game-releases__confirm-safe"><ShieldCheck :size="18" />{{ t('visualGameAdmin.gameReleases.noUserData') }}</div>
      </div>
      <template #footer>
        <Button variant="ghost" :disabled="publishing" @click="confirmOpen = false">{{ t('visualGameAdmin.common.cancel') }}</Button>
        <Button variant="primary" :loading="publishing" @click="publish">{{ t('visualGameAdmin.gameReleases.confirmPublish') }}</Button>
      </template>
    </Modal>
  </div>
</template>

<style scoped>
.game-releases { display: grid; gap: var(--drama-space-xl); }
.game-releases__selector :deep(.drama-card__body) { display: flex; align-items: center; justify-content: space-between; gap: var(--drama-space-xl); }
.game-releases__selector-copy { display: grid; gap: var(--drama-space-xs); max-width: 620px; }
.game-releases__selector-copy h2,
.game-releases__selector-copy p,
.game-releases__environment h2,
.game-releases__action h2,
.game-releases__result h3,
.game-releases__result p { margin: 0; }
.game-releases__selector-copy p,
.game-releases__action p,
.game-releases__result p { color: var(--drama-color-text-muted); }
.game-releases__eyebrow { color: var(--drama-color-primary); font-size: var(--drama-font-size-xs); font-weight: var(--drama-font-weight-bold); letter-spacing: .08em; text-transform: uppercase; }
.game-releases__state :deep(.drama-card__body) { min-height: 240px; display: grid; place-items: center; align-content: center; gap: var(--drama-space-md); text-align: center; }
.game-releases__state--error { color: var(--drama-color-danger); }
.game-releases__route { display: grid; grid-template-columns: 1fr minmax(100px, 220px) 1fr; align-items: center; gap: var(--drama-space-lg); padding-inline: var(--drama-space-xl); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); font-weight: var(--drama-font-weight-bold); letter-spacing: .08em; text-align: center; text-transform: uppercase; }
.game-releases__route div { display: flex; align-items: center; color: var(--drama-color-primary); }
.game-releases__route i { height: 1px; flex: 1; background: var(--drama-color-border); }
.game-releases__compare { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--drama-space-lg); }
.game-releases__environment { overflow: hidden; padding: 0; }
.game-releases__environment--stage { border-color: var(--drama-color-primary); }
.game-releases__environment header { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: var(--drama-space-md); padding: var(--drama-space-lg); border-bottom: 1px solid var(--drama-color-border); background: var(--drama-color-surface-alt); }
.game-releases__environment header span { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.game-releases__environment-icon,
.game-releases__action-icon,
.game-releases__result-icon { display: grid; place-items: center; width: 42px; height: 42px; border-radius: var(--drama-radius-md); background: var(--vgs-gold-dim); color: var(--drama-color-primary); }
.game-releases__environment dl { margin: 0; padding: var(--drama-space-lg); }
.game-releases__environment dl div { display: grid; grid-template-columns: minmax(120px, .8fr) minmax(0, 1.2fr); gap: var(--drama-space-md); padding: var(--drama-space-sm) 0; border-bottom: 1px solid var(--drama-color-border); }
.game-releases__environment dl div:last-child { border-bottom: 0; }
.game-releases__environment dt { color: var(--drama-color-text-muted); }
.game-releases__environment dd { margin: 0; text-align: right; overflow-wrap: anywhere; }
.game-releases__environment code,
.game-releases__result code,
.game-releases__confirm code { font-size: var(--drama-font-size-xs); }
.game-releases__connection-note { display: flex; gap: var(--drama-space-sm); align-items: flex-start; padding: var(--drama-space-xl); color: var(--drama-color-text-muted); }
.game-releases__connection-note p { margin: 0; }
.game-releases__action :deep(.drama-card__body) { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: var(--drama-space-xl); }
.game-releases__publish-error { grid-column: 1 / -1; display: flex; align-items: center; gap: var(--drama-space-xs); width: 100%; margin: 0; color: var(--drama-color-danger) !important; }
.game-releases__action-copy { display: flex; align-items: center; gap: var(--drama-space-lg); }
.game-releases__action-copy > div:last-child { display: grid; gap: var(--drama-space-xs); }
.game-releases__read-only { color: var(--drama-color-warning) !important; }
.game-releases__result { border-color: var(--drama-color-success); }
.game-releases__result :deep(.drama-card__body) { display: flex; gap: var(--drama-space-lg); align-items: flex-start; }
.game-releases__result-icon { background: var(--vgs-gold-dim); color: var(--drama-color-success); }
.game-releases__result > div:last-child { display: grid; gap: var(--drama-space-xs); }
.game-releases__confirm { display: grid; gap: var(--drama-space-lg); }
.game-releases__confirm-version { display: grid; gap: var(--drama-space-xs); padding: var(--drama-space-lg); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface-alt); }
.game-releases__confirm-version strong { color: var(--drama-color-primary); font-size: var(--drama-font-size-xl); }
.game-releases__confirm-safe { display: flex; align-items: center; gap: var(--drama-space-sm); color: var(--drama-color-success); }
.game-releases__history :deep(.drama-card__body) { display: grid; gap: var(--drama-space-lg); }
.game-releases__history-header { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--drama-space-lg); }
.game-releases__history-header h2,
.game-releases__history-header p,
.game-releases__history-empty { margin: 0; }
.game-releases__history-header p,
.game-releases__history-empty { color: var(--drama-color-text-muted); }
.game-releases__history-list { display: grid; gap: var(--drama-space-md); }
.game-releases__release { display: grid; gap: var(--drama-space-md); padding: var(--drama-space-lg); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-lg); background: var(--drama-color-surface-alt); }
.game-releases__release-head { display: flex; justify-content: space-between; gap: var(--drama-space-lg); }
.game-releases__release-head > div { display: grid; gap: var(--drama-space-xs); }
.game-releases__release-head code { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.game-releases__release-meta { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--drama-space-md); margin: 0; }
.game-releases__release-meta div { display: grid; gap: var(--drama-space-xs); }
.game-releases__release-meta dt { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.game-releases__release-meta dd { margin: 0; overflow-wrap: anywhere; }
.game-releases__release-error { margin: 0; padding: var(--drama-space-sm) var(--drama-space-md); border-radius: var(--drama-radius-md); background: color-mix(in srgb, var(--drama-color-danger) 10%, transparent); color: var(--drama-color-danger); }
.game-releases__logs { display: grid; gap: var(--drama-space-sm); margin: 0; padding: 0; list-style: none; }
.game-releases__logs li { display: grid; grid-template-columns: auto 1fr; gap: var(--drama-space-sm); align-items: start; }
.game-releases__log-dot { width: 8px; height: 8px; margin-top: 6px; border-radius: 50%; background: var(--drama-color-primary); }
.game-releases__logs li div { display: flex; justify-content: space-between; gap: var(--drama-space-md); }
.game-releases__logs small { color: var(--drama-color-text-muted); white-space: nowrap; }
@media (max-width: 860px) {
  .game-releases__selector :deep(.drama-card__body),
  .game-releases__action :deep(.drama-card__body) { align-items: stretch; flex-direction: column; }
  .game-releases__compare { grid-template-columns: 1fr; }
  .game-releases__route { display: none; }
  .game-releases__release-meta { grid-template-columns: 1fr; }
  .game-releases__logs li div { display: grid; gap: var(--drama-space-xs); }
}
</style>
